import type { ApiResponse, KOApiResponse, ServerError } from 'types/request.type';

export const API_BASE_URL = process.env.API_GATEWAY;

export function checkForServerError(r: unknown): r is KOApiResponse {
  if (typeof r === 'object' && r !== null) {
    return 'status' in r && r.status === 'KO';
  }
  return false;
}

export function getServerError(r: KOApiResponse, defaultError?: ServerError) {
  if (r.errors?.[0] !== undefined) {
    const e = {
      ...r.errors[0],
      code: r.errors[0].code.toLowerCase(),
    };
    return e;
  }
  return defaultError ?? { code: 'unknown_error', message: 'unknown error' };
}

export async function handleFetchResponse<T extends ApiResponse>(
  response: Response,
  defaultError?: ServerError,
): Promise<{ ok: false; error: ServerError } | { ok: true; json: T; error?: never }> {
  const isJson = response.headers.get('content-type')?.includes('application/json');

  if (!isJson) {
    return { ok: false, error: { code: 'json_error', message: 'response was not json' } };
  }
  if (!response.ok) {
    let error = await response.json();
    error = getServerError(error, defaultError);
    return { ok: false, error };
  }
  const json = await response.json();
  if (checkForServerError(json)) {
    return { ok: false, error: getServerError(json) };
  }
  return { ok: true, json };
}

export function getErrorFromResponse(data: KOApiResponse) {
  if (data?.errors?.length > 0) {
    return data.errors[0];
  }
  return {
    code: 'MISSING_ERROR',
    message: 'Unexpected Error',
  };
}

export function isDefaultError(code: string) {
  return code === 'MISSING_ERROR';
}

/*
 * @param {string | null} accept
 * @returns {{locale: string | null, language: string | null, country: string | null}}
 */
export function parseAcceptLanguageHeader(accept?: string | null) {
  if (accept === null || accept === undefined) {
    return {
      locale: null,
      language: null,
      country: null,
    };
  }
  const acceptString = accept.split(';')[0];

  const localeMatch = new RegExp(/([a-z]){2}-[A-Z]{2}?/).exec(acceptString);
  if (Array.isArray(localeMatch)) {
    const [language, country] = localeMatch[0].split('-');
    return {
      locale: localeMatch[0],
      language,
      country,
    };
  }
  // if we only find language in the accept string
  const languageMatch = new RegExp(/([a-z]){2}/).exec(acceptString);
  if (Array.isArray(languageMatch)) {
    return {
      locale: null,
      language: languageMatch[0],
      country: null,
    };
  }

  return {
    locale: null,
    language: null,
    country: null,
  };
}
